"use client"

import { ChevronDown, Eye, FileText, MessageSquare, Trash2, Zap } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import type { UnifiedCall } from "@/types/unified-call"
import type { TranscriptMessage } from "@/types/calls"
import { useTranslations } from "next-intl"


export interface CallActionsMenuProps {
  call: UnifiedCall
  isSuperUser: boolean
  canDelete: boolean
  onViewTranscript: (callId: string) => void
  onViewDetails?: (callId: string) => void
  onViewToolCalls: (transcript: TranscriptMessage[]) => void
  onDelete: (callId: string) => void
  feedbackStatus?: "pending" | "in_progress" | "resolved" | null
  onAddFeedback?: (call: UnifiedCall) => void
}

export function CallActionsMenu({
  call, isSuperUser, canDelete, onViewTranscript, onViewDetails,
  onViewToolCalls, onDelete, feedbackStatus, onAddFeedback,
}: CallActionsMenuProps) {
  const tUi = useTranslations("Calls.CallsCallActionsMenu")

  const isVoiceAgent = call.type === "voice_agent"
  const transcript = isVoiceAgent ? call.voiceAgentData?.transcript : call.pbxData?.linkedAiCall?.transcript
  const toolCallCount = countToolCalls(transcript)
  const hasTranscript = isVoiceAgent || Boolean(call.pbxData?.linkedAiCall)

  if (!hasTranscript) return null

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-12 w-full justify-between px-4 text-base sm:h-9 sm:w-auto sm:justify-center sm:px-3 sm:text-sm">
          <span><span key={tUi("actions")}>{tUi("actions")}</span></span>
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        collisionPadding={16}
        className="max-h-[min(24rem,var(--radix-dropdown-menu-content-available-height))] w-[calc(100vw-2rem)] max-w-sm overscroll-contain sm:w-64"
      >
        <DropdownMenuItem onSelect={() => onViewTranscript(call.id)}>
          <FileText className="h-4 w-4" /><span><span key={tUi("viewTranscript")}>{tUi("viewTranscript")}</span></span>
        </DropdownMenuItem>
        {toolCallCount > 0 && transcript && (
          <DropdownMenuItem onSelect={() => onViewToolCalls(transcript)}>
            <Zap className="h-4 w-4 text-amber-500" /><span><span key={tUi("viewToolUsage")}>{tUi("viewToolUsage")}</span></span>
            <span className="ml-auto rounded-full bg-amber-500 px-1.5 text-xs text-white">{toolCallCount}</span>
          </DropdownMenuItem>
        )}
        {isVoiceAgent && onAddFeedback && (
          <DropdownMenuItem onSelect={() => onAddFeedback(call)} className={feedbackStatus ? "text-orange-500" : undefined}>
            <MessageSquare className="h-4 w-4" /><span key={feedbackStatus ? tUi("viewFeedback") : tUi("addFeedback")}>{feedbackStatus ? tUi("viewFeedback") : tUi("addFeedback")}</span>
            {feedbackStatus && feedbackStatus !== "resolved" && (
              <span className="ml-auto h-2 w-2 rounded-full bg-orange-500" aria-label={tUi("unresolvedFeedback")} />
            )}
          </DropdownMenuItem>
        )}
        {isSuperUser && onViewDetails && isVoiceAgent && (
          <DropdownMenuItem onSelect={() => onViewDetails(call.id)}>
            <Eye className="h-4 w-4" /><span><span key={tUi("viewRawData")}>{tUi("viewRawData")}</span></span>
          </DropdownMenuItem>
        )}
        {canDelete && isVoiceAgent && (
          <DropdownMenuItem onSelect={() => onDelete(call.id)} variant="destructive">
            <Trash2 className="h-4 w-4" /><span><span key={tUi("deleteCall")}>{tUi("deleteCall")}</span></span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function countToolCalls(transcript: TranscriptMessage[] | undefined): number {
  if (!Array.isArray(transcript)) return 0
  return transcript.reduce((count, message) => {
    if (!Array.isArray(message.tool_calls)) return count
    return count + message.tool_calls.length
  }, 0)
}
